import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Redirect component for legacy /s/:code URLs
function LegacyScanRedirect() {
  const { code } = useParams<{ code: string }>();
  return <Navigate to={`/DiscoverThePark/${code}`} replace />;
}

// Layouts
import AdminLayout from './components/layout/AdminLayout';
import StoreLayout from './components/layout/StoreLayout';
import PublicLayout from './components/layout/PublicLayout';

// Unified login
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';

// Admin pages
import AdminDashboardPage from './pages/admin/DashboardPage';
import AdminCampaignsPage from './pages/admin/CampaignsPage';
import AdminStoresPage from './pages/admin/StoresPage';
import AdminCustomersPage from './pages/admin/CustomersPage';
import AdminAuditLogsPage from './pages/admin/AuditLogsPage';
import AdminStoreSettingsPage from './pages/admin/StoreSettingsPage';
import AdminQRCodesPage from './pages/admin/QRCodesPage';
import AdminQRCodeDesignerPage from './pages/admin/QRCodeDesignerPage';
import AdminCampaignSettingsPage from './pages/admin/CampaignSettingsPage';
import AdminCampaignRunViewPage from './pages/admin/CampaignRunViewPage';
import AdminWinnersPage from './pages/admin/WinnersPage';
import AdminDrawWinnersPage from './pages/admin/DrawWinnersPage';

// Store pages
import StoreDashboardPage from './pages/store/DashboardPage';
import StoreProfilePage from './pages/store/ProfilePage';
import StoreCampaignsPage from './pages/store/CampaignsPage';
import StoreQRCodesPage from './pages/store/QRCodesPage';
import StoreCustomersPage from './pages/store/CustomersPage';
import StoreWinnersPage from './pages/store/WinnersPage';

// Public pages
import ScanPage from './pages/public/ScanPage';
import CampaignPage from './pages/public/CampaignPage';
import InviteAcceptPage from './pages/public/InviteAcceptPage';
import NotFoundPage from './pages/NotFoundPage';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/DiscoverThePark/:code" element={<PublicLayout><ScanPage /></PublicLayout>} />
      {/* Legacy redirect for old QR codes */}
      <Route path="/s/:code" element={<LegacyScanRedirect />} />
      <Route path="/campaign/:runId" element={<CampaignPage />} />
      <Route path="/invite/:token" element={<PublicLayout><InviteAcceptPage /></PublicLayout>} />

      {/* Unified login */}
      <Route path="/login" element={<LoginPage />} />
      {/* Legacy redirects */}
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/store/auth" element={<Navigate to="/login" replace />} />

      {/* Admin draw page (full screen, outside layout) */}
      <Route path="/admin/draw/:runId" element={<AdminDrawWinnersPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboardPage />} />
        <Route path="campaigns" element={<AdminCampaignsPage />} />
        <Route path="campaigns/:campaignId/settings" element={<AdminCampaignSettingsPage />} />
        <Route path="campaigns/:campaignId/runs/:runId" element={<AdminCampaignRunViewPage />} />
        <Route path="stores" element={<AdminStoresPage />} />
        <Route path="customers" element={<AdminCustomersPage />} />
        <Route path="winners" element={<AdminWinnersPage />} />
        <Route path="audit-logs" element={<AdminAuditLogsPage />} />
        <Route path="store-settings" element={<AdminStoreSettingsPage />} />
        <Route path="qrcodes" element={<AdminQRCodesPage />} />
        <Route path="qrcodes/:qrcodeId" element={<AdminQRCodeDesignerPage />} />
      </Route>

      {/* Store routes */}
      <Route path="/store" element={<StoreLayout />}>
        <Route index element={<Navigate to="/store/dashboard" replace />} />
        <Route path="dashboard" element={<StoreDashboardPage />} />
        <Route path="profile" element={<StoreProfilePage />} />
        <Route path="campaigns" element={<StoreCampaignsPage />} />
        <Route path="qrcodes" element={<StoreQRCodesPage />} />
        <Route path="customers" element={<StoreCustomersPage />} />
        <Route path="winners" element={<StoreWinnersPage />} />
      </Route>

      {/* Public landing page */}
      <Route path="/" element={<HomePage />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
